// The socket opens only after `/api/me` says who this is; an unauthenticated upgrade is refused.
import type { Session } from '@alphadesk/web/contract.ts';
import { legalOf, PAGES, routeOf } from '@alphadesk/web/links.ts';
import { lazy, Suspense, useState } from 'react';
import { BrokerProvider } from './components/Broker.tsx';
import { FallbacksProvider } from './components/Fallbacks.tsx';
import { FeedbackProvider } from './components/Feedback.tsx';
import { InviteProvider } from './components/Invite.tsx';
import { JoinPrompt } from './components/Join.tsx';
import { LookProvider } from './components/Look.tsx';
import { Shell } from './components/Shell.tsx';
import { Toasts } from './components/Toast.tsx';
import { ViewingBanner } from './components/ViewingAs.tsx';
import { useHome } from './lib/home.ts';
import { openAt, useRoute } from './lib/router.ts';
import { useScopeInAddress } from './lib/scope.ts';
import { useSession } from './lib/session.ts';
import { useStream } from './lib/stream.ts';
import { useUsage } from './lib/useUsage.ts';
import { Opening } from './routes/route.tsx';
import { ROUTE_TABLE } from './routes/table.ts';

// Outside the console: somebody signed out never loads it, and somebody signed in never loads these.
const SignIn = lazy(() => import('./components/Landing.tsx').then((m) => ({ default: m.SignIn })));
const Welcome = lazy(() => import('./components/Gate.tsx').then((m) => ({ default: m.Welcome })));
const Broken = lazy(() => import('./components/Gate.tsx').then((m) => ({ default: m.Broken })));
const Legal = lazy(() => import('./components/Legal.tsx').then((m) => ({ default: m.LegalPage })));
const NotFound = lazy(() =>
  import('./components/pages/NotFound.tsx').then((m) => ({ default: m.NotFoundPage })),
);

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <span className="text-sm text-ink-faint">Loading…</span>
    </div>
  );
}

function Live({ session }: { session: Session }) {
  const stream = useStream();
  const route = useRoute();
  const viewer = stream.viewer ?? session.viewer;
  const books = stream.books.length === 0 ? session.books : stream.books;
  useUsage(session.posthog, viewer, route);
  useScopeInAddress(route.path, route.query.toString());
  const home = useHome(route.path === PAGES.home || route.path === PAGES.trades, stream);

  const at = routeOf(route.path);
  const entry = at && ROUTE_TABLE.find((one) => one.path === at.route && one.guard(viewer));
  const props = at && {
    viewer,
    stream: stream.books.length === 0 ? { ...stream, books } : stream,
    books,
    home,
    at,
  };
  const Page = entry?.component;
  const Rail = entry?.rail;

  return (
    <Toasts>
      <BrokerProvider viewer={viewer}>
        <FallbacksProvider viewer={viewer}>
          <FeedbackProvider>
            <InviteProvider viewer={viewer} viewing={session.viewing}>
              <Shell
                viewer={viewer}
                connection={stream.connection}
                notifications={stream.notifications}
                unread={stream.unread}
                banner={session.viewing && <ViewingBanner name={viewer.name} />}
                rail={
                  Rail && props ? (
                    <Suspense
                      fallback={<div aria-hidden className="hidden w-80 shrink-0 xl:block" />}
                    >
                      <Rail {...props} />
                    </Suspense>
                  ) : undefined
                }
              >
                <JoinPrompt viewer={viewer} />
                <Suspense fallback={<Opening />}>
                  {Page && props ? <Page {...props} /> : <NotFound path={route.path} />}
                </Suspense>
              </Shell>
            </InviteProvider>
          </FeedbackProvider>
        </FallbacksProvider>
      </BrokerProvider>
    </Toasts>
  );
}

export function App({ signedOut }: { signedOut: boolean }) {
  // Ahead of every `useRoute`, so an old address never paints.
  useState(openAt);
  const legal = legalOf(useRoute().path);
  return (
    // Outside the gate: the look belongs to the browser, not the session.
    <LookProvider>
      <Suspense fallback={<Splash />}>
        {legal ? <Legal page={legal} /> : <Doorway signedOut={signedOut} />}
      </Suspense>
    </LookProvider>
  );
}

/** Signed out, any address shows the landing page: `/`, where an invite link lands, or a deep link to come back to. */
function Doorway({ signedOut }: { signedOut: boolean }) {
  const session = useSession(signedOut);

  switch (session.status) {
    case 'loading':
      return <Splash />;
    case 'out':
      return <SignIn reason={new URLSearchParams(window.location.search).get('signin')} />;
    case 'denied':
      return (
        <>
          {session.viewing && <ViewingBanner name={session.name} />}
          <Welcome
            denial={session.denial}
            name={session.name}
            email={session.email}
            picture={session.picture}
            invitedBy={session.invitedBy}
            onAsked={session.reask}
          />
        </>
      );
    case 'error':
      return <Broken />;
    case 'in':
      return <Live session={session.session} />;
  }
}
